import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useSpring,
} from "framer-motion";
import {
  Upload,
  Scan,
  CheckCircle,
  Trophy,
  Heart,
  MessageCircle,
  Share2,
  Users,
  Zap,
  Star,
  TrendingUp,
  Clock,
  Target,
  Sparkles,
  ArrowRight,
  Play,
  Send,
  Flame,
  Gift,
  Shield,
  Gamepad2,
  Store,
  ChevronRight,
  Loader2,
  MessageSquare,
  Newspaper,
  Lightbulb,
  HelpCircle,
  Rocket,
  Crown,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { Button } from "../components/common/Button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ref, push, onValue, off } from "firebase/database";
import { database, dbGet, dbUpdate } from "../config/firebase";
import { useTestMode } from "../contexts/TestModeContext";
import { OGHubMessage } from "../types";
import { formatDistanceToNow } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import emailjs from "@emailjs/browser";

// ============================================
// ANIMATED BACKGROUND PARTICLES
// ============================================

const FloatingParticles: React.FC = () => {
  const particles = Array.from({ length: 30 });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            background: `rgba(34, 197, 94, ${Math.random() * 0.3 + 0.1})`,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, Math.random() * 20 - 10, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: Math.random() * 4 + 3,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// GLOWING ORB BACKGROUND
// ============================================

const GlowingOrbs: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 70%)",
          left: "-10%",
          top: "-20%",
        }}
        animate={{
          x: [0, 50, 0],
          y: [0, 30, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-[500px] h-[500px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)",
          right: "-10%",
          bottom: "-10%",
        }}
        animate={{
          x: [0, -40, 0],
          y: [0, -20, 0],
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(168,85,247,0.08) 0%, transparent 70%)",
          right: "20%",
          top: "10%",
        }}
        animate={{
          x: [0, 30, 0],
          y: [0, 40, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />
    </div>
  );
};

// ============================================
// CONFETTI EXPLOSION
// ============================================

const Confetti: React.FC<{ count?: number }> = ({ count = 150 }) => {
  const colors = [
    "#22C55E",
    "#FFD700",
    "#EF4444",
    "#3B82F6",
    "#F59E0B",
    "#EC4899",
    "#8B5CF6",
    "#06B6D4",
  ];

  return (
    <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
      {Array.from({ length: count }).map((_, i) => {
        const isLeft = Math.random() > 0.5;
        return (
          <motion.div
            key={i}
            className="absolute"
            style={{
              width: Math.random() * 10 + 5,
              height: Math.random() * 10 + 5,
              borderRadius: Math.random() > 0.5 ? "50%" : "2px",
              left: isLeft
                ? `${Math.random() * 30}%`
                : `${70 + Math.random() * 30}%`,
              backgroundColor: colors[i % colors.length],
            }}
            initial={{
              top: "50%",
              opacity: 1,
              rotate: 0,
              scale: 0,
            }}
            animate={{
              top: ["50%", `${-20 + Math.random() * 140}%`],
              left: isLeft
                ? [`${Math.random() * 30}%`, `${20 + Math.random() * 60}%`]
                : [
                    `${70 + Math.random() * 30}%`,
                    `${20 + Math.random() * 60}%`,
                  ],
              opacity: [1, 1, 0],
              rotate: [0, Math.random() * 720 - 360],
              scale: [0, 1, 0.5],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              delay: Math.random() * 0.3,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          />
        );
      })}
    </div>
  );
};

// ============================================
// FLOATING CARD WRAPPER
// ============================================

interface FloatingCardProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  intensity?: number;
}

const FloatingCard: React.FC<FloatingCardProps> = ({
  children,
  className = "",
  delay = 0,
  intensity = 1,
}) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useTransform(
    mouseY,
    [-300, 300],
    [5 * intensity, -5 * intensity]
  );
  const rotateY = useTransform(
    mouseX,
    [-300, 300],
    [-5 * intensity, 5 * intensity]
  );

  const springConfig = { stiffness: 150, damping: 15 };
  const rotateXSpring = useSpring(rotateX, springConfig);
  const rotateYSpring = useSpring(rotateY, springConfig);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    mouseX.set(e.clientX - centerX);
    mouseY.set(e.clientY - centerY);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.5, ease: "easeOut" }}
      style={{
        rotateX: rotateXSpring,
        rotateY: rotateYSpring,
        transformPerspective: 1000,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// PULSING GLOW RING
// ============================================

const PulsingRing: React.FC<{ size?: number; color?: string }> = ({
  size = 200,
  color = "#22C55E",
}) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border-2"
          style={{
            width: size,
            height: size,
            borderColor: color,
            opacity: 0.3 - i * 0.1,
          }}
          animate={{
            scale: [1, 1.5 + i * 0.3, 1],
            opacity: [0.3 - i * 0.1, 0, 0.3 - i * 0.1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.4,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// SIMULATION STAGES CONFIG
// ============================================

type SimulationStage =
  | "intro"
  | "uploading"
  | "analyzing"
  | "extracting"
  | "verifying"
  | "posting"
  | "live"
  | "winning"
  | "celebration"
  | "welcome";

// PICKS data - no wagers, odds, or payout (public Explore page view)
const mockPicksData = {
  user: {
    reputation: 87,
    streak: 5,
  },
  picks: [
    {
      team: "Chiefs",
      opponent: "Raiders",
      pick: "Chiefs to Win",
      status: "pending",
    },
    {
      team: "Lakers",
      opponent: "Celtics",
      pick: "Lakers to Win",
      status: "pending",
    },
    {
      team: "Yankees",
      opponent: "Red Sox",
      pick: "Over Total Runs",
      status: "pending",
    },
  ],
};

const mockChatMessages = [
  { user: "@parlay_king", msg: "Let's gooo! 🔥", delay: 800 },
  { user: "@betting_beast", msg: "Chiefs looking strong today", delay: 2000 },
  { user: "@sports_fan_22", msg: "Tailing this one! 💰", delay: 3500 },
  { user: "@winner_circle", msg: "Lakers ML is the play", delay: 5000 },
  { user: "@clutch_bettor", msg: "This parlay is fire 🔥🔥", delay: 6500 },
  { user: "@sharp_money", msg: "Good value on the over", delay: 8000 },
];

const mockNewsHeadlines = [
  {
    headline: "🏈 Chiefs RB cleared to play tonight",
    delay: 500,
    type: "breaking",
  },
  {
    headline: "🏀 Lakers announce starting lineup change",
    delay: 2500,
    type: "update",
  },
  {
    headline: "⚾ Yankees ace confirmed for tonight's game",
    delay: 4500,
    type: "news",
  },
  {
    headline: "☀️ Weather: Perfect conditions for all games",
    delay: 6500,
    type: "info",
  },
  {
    headline: "📊 78% of experts picking Chiefs spread",
    delay: 8500,
    type: "stats",
  },
];

const stageConfig: Record<
  SimulationStage,
  { title: string; subtitle: string; duration: number }
> = {
  intro: { title: "", subtitle: "", duration: 0 },
  uploading: {
    title: "Uploading Slip",
    subtitle: "Securely uploading your screenshot...",
    duration: 1800,
  },
  analyzing: {
    title: "AI Analysis",
    subtitle: "Scanning slip with advanced OCR...",
    duration: 2200,
  },
  extracting: {
    title: "Extracting Picks",
    subtitle: "Identifying your selections...",
    duration: 2000,
  },
  verifying: {
    title: "Verifying Picks",
    subtitle: "Cross-referencing with live games...",
    duration: 1500,
  },
  posting: {
    title: "Going Live",
    subtitle: "Publishing to the community...",
    duration: 1200,
  },
  live: {
    title: "Your Picks are LIVE!",
    subtitle: "Watch the community engage in real-time",
    duration: 12000,
  },
  winning: {
    title: "Final Pick...",
    subtitle: "The moment of truth",
    duration: 3000,
  },
  celebration: {
    title: "ALL PICKS HIT! 🔥",
    subtitle: "Your streak continues!",
    duration: 4000,
  },
  welcome: { title: "", subtitle: "", duration: 0 },
};

// ============================================
// DRAMATIC SIMULATION OVERLAY
// ============================================

interface SimulationOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  userName: string;
  userInitial: string;
}

const SimulationOverlay: React.FC<SimulationOverlayProps> = ({
  isOpen,
  onClose,
  onComplete,
  userName,
  userInitial,
}) => {
  const [stage, setStage] = useState<SimulationStage>("intro");
  const [progress, setProgress] = useState(0);
  const [likes, setLikes] = useState(0);
  const [comments, setComments] = useState(0);
  const [tails, setTails] = useState(0);
  const [picks, setPicks] = useState(mockPicksData.picks);
  const [showConfetti, setShowConfetti] = useState(false);
  const [chatMessages, setChatMessages] = useState<typeof mockChatMessages>([]);
  const [newsHeadlines, setNewsHeadlines] = useState<typeof mockNewsHeadlines>(
    []
  );
  const [streak, setStreak] = useState(mockPicksData.user.streak);

  const isProcessing = [
    "uploading",
    "analyzing",
    "extracting",
    "verifying",
    "posting",
  ].includes(stage);
  const isLivePhase = ["live", "winning", "celebration"].includes(stage);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStage("intro");
      setProgress(0);
      setLikes(0);
      setComments(0);
      setTails(0);
      setPicks(mockPicksData.picks.map((p) => ({ ...p, status: "pending" })));
      setChatMessages([]);
      setNewsHeadlines([]);
      setShowConfetti(false);
      setStreak(mockPicksData.user.streak);
    }
  }, [isOpen]);

  // Stage progression
  const advanceStage = useCallback(() => {
    const stages: SimulationStage[] = [
      "uploading",
      "analyzing",
      "extracting",
      "verifying",
      "posting",
      "live",
      "winning",
      "celebration",
      "welcome",
    ];
    const currentIndex = stages.indexOf(stage);
    if (currentIndex < stages.length - 1) {
      setStage(stages[currentIndex + 1]);
    }
  }, [stage]);

  // Auto-advance stages
  useEffect(() => {
    if (stage === "intro" || stage === "welcome") return;
    const config = stageConfig[stage];
    if (config.duration > 0) {
      const timer = setTimeout(advanceStage, config.duration);
      return () => clearTimeout(timer);
    }
  }, [stage, advanceStage]);

  // Progress animation
  useEffect(() => {
    if (!isProcessing) return;
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 1.5, 100));
    }, stageConfig[stage].duration / 70);
    return () => clearInterval(interval);
  }, [stage, isProcessing]);

  // Engagement simulation
  useEffect(() => {
    if (stage !== "live") return;
    const likeInterval = setInterval(
      () => setLikes((prev) => prev + Math.floor(Math.random() * 4) + 1),
      350
    );
    const commentInterval = setInterval(
      () => setComments((prev) => prev + 1),
      700
    );
    const tailInterval = setInterval(() => setTails((prev) => prev + 1), 1100);
    return () => {
      clearInterval(likeInterval);
      clearInterval(commentInterval);
      clearInterval(tailInterval);
    };
  }, [stage]);

  // Chat messages
  useEffect(() => {
    if (stage !== "live") return;
    setChatMessages([]);
    const timers = mockChatMessages.map((msg) =>
      setTimeout(() => setChatMessages((prev) => [...prev, msg]), msg.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [stage]);

  // News headlines
  useEffect(() => {
    if (stage !== "live") return;
    setNewsHeadlines([]);
    const timers = mockNewsHeadlines.map((news) =>
      setTimeout(() => setNewsHeadlines((prev) => [...prev, news]), news.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [stage]);

  // Pick updates
  useEffect(() => {
    if (stage !== "live") return;
    const timers = [
      setTimeout(
        () =>
          setPicks((prev) =>
            prev.map((p, i) => (i === 0 ? { ...p, status: "won" } : p))
          ),
        4000
      ),
      setTimeout(
        () =>
          setPicks((prev) =>
            prev.map((p, i) => (i === 1 ? { ...p, status: "won" } : p))
          ),
        8000
      ),
    ];
    return () => timers.forEach(clearTimeout);
  }, [stage]);

  // Final pick + confetti
  useEffect(() => {
    if (stage !== "winning") return;
    const timer = setTimeout(() => {
      setPicks((prev) =>
        prev.map((p, i) => (i === 2 ? { ...p, status: "won" } : p))
      );
    }, 2000);
    return () => clearTimeout(timer);
  }, [stage]);

  useEffect(() => {
    if (stage === "celebration") {
      setShowConfetti(true);
      setStreak((prev) => prev + 1);
    }
  }, [stage]);

  const startSimulation = () => setStage("uploading");

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl overflow-hidden"
    >
      {/* Background Effects */}
      <GlowingOrbs />
      <FloatingParticles />
      {showConfetti && <Confetti count={200} />}

      {/* Close Button */}
      {stage !== "welcome" && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          onClick={onClose}
          className="absolute top-6 right-6 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X size={24} className="text-white/70" />
        </motion.button>
      )}

      {/* Intro Screen */}
      <AnimatePresence mode="wait">
        {stage === "intro" && (
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="text-center max-w-lg px-6">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="relative mb-8"
              >
                <div className="w-32 h-32 mx-auto bg-gradient-to-br from-primary via-green-400 to-emerald-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-primary/40">
                  <Upload size={48} className="text-white" />
                </div>
                <PulsingRing size={180} />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl md:text-5xl font-black text-white mb-4"
              >
                Experience the Magic
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg text-gray-400 mb-8"
              >
                Watch how a single screenshot transforms into a live,
                community-powered experience
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  variant="primary"
                  size="lg"
                  icon={<Rocket size={24} />}
                  onClick={startSimulation}
                  className="px-10 py-4 text-lg font-bold shadow-xl shadow-primary/40 hover:shadow-primary/60 transition-all hover:scale-105"
                >
                  Start Simulation
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-8 flex justify-center gap-6 text-sm text-gray-500"
              >
                {[
                  "AI Scanning",
                  "Live Updates",
                  "Community",
                  "Celebrations",
                ].map((item, i) => (
                  <motion.span
                    key={item}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + i * 0.1 }}
                    className="flex items-center gap-1"
                  >
                    <Sparkles size={12} className="text-primary" />
                    {item}
                  </motion.span>
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Processing Stages */}
        {isProcessing && (
          <motion.div
            key="processing"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="text-center max-w-md px-6">
              <motion.div
                className="relative mb-8"
                animate={{
                  rotateY: stage === "analyzing" ? [0, 360] : 0,
                }}
                transition={{
                  duration: 2,
                  repeat: stage === "analyzing" ? Infinity : 0,
                  ease: "linear",
                }}
              >
                <div className="w-28 h-28 mx-auto bg-gradient-to-br from-primary/20 to-emerald-500/20 rounded-3xl flex items-center justify-center border border-primary/30 backdrop-blur-sm">
                  {stage === "uploading" && (
                    <Upload size={40} className="text-primary" />
                  )}
                  {stage === "analyzing" && (
                    <Scan size={40} className="text-primary" />
                  )}
                  {stage === "extracting" && (
                    <Target size={40} className="text-primary" />
                  )}
                  {stage === "verifying" && (
                    <CheckCircle size={40} className="text-primary" />
                  )}
                  {stage === "posting" && (
                    <Sparkles size={40} className="text-primary" />
                  )}
                </div>
                <PulsingRing size={160} />
              </motion.div>

              <motion.h2
                key={stage}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl font-bold text-white mb-2"
              >
                {stageConfig[stage].title}
              </motion.h2>

              <motion.p
                key={`${stage}-sub`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-gray-400 mb-8"
              >
                {stageConfig[stage].subtitle}
              </motion.p>

              {/* Progress Bar */}
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-primary via-green-400 to-emerald-400"
                    style={{ width: `${progress}%` }}
                    transition={{ duration: 0.1 }}
                  />
                </div>
                <p className="text-center text-sm text-gray-500 mt-3">
                  {Math.round(progress)}%
                </p>
              </div>

              {/* Picks Preview during extracting */}
              {stage === "extracting" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10 text-left"
                >
                  {mockPicksData.picks.map((pick, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + i * 0.2 }}
                      className="flex justify-between py-1 text-sm"
                    >
                      <span className="text-gray-500">
                        {pick.team} vs {pick.opponent}
                      </span>
                      <span className="text-primary font-medium">
                        {pick.pick}
                      </span>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* Live Phase */}
        {isLivePhase && (
          <motion.div
            key="live"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 p-4 md:p-8 overflow-auto"
          >
            <div className="max-w-6xl mx-auto h-full flex flex-col">
              {/* Status Header */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-center mb-6"
              >
                <motion.div
                  className={`inline-flex items-center gap-3 px-6 py-3 rounded-full font-bold text-lg ${
                    stage === "celebration"
                      ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-black"
                      : stage === "winning"
                      ? "bg-gradient-to-r from-yellow-500/80 to-orange-500/80 text-black"
                      : "bg-gradient-to-r from-primary to-emerald-500 text-white"
                  }`}
                  animate={
                    stage === "winning" || stage === "celebration"
                      ? {
                          scale: [1, 1.05, 1],
                          boxShadow: [
                            "0 0 20px rgba(234,179,8,0.3)",
                            "0 0 40px rgba(234,179,8,0.6)",
                            "0 0 20px rgba(234,179,8,0.3)",
                          ],
                        }
                      : {}
                  }
                  transition={{
                    duration: 0.5,
                    repeat: stage === "winning" ? Infinity : 0,
                  }}
                >
                  {stage === "celebration" ? (
                    <Trophy size={24} />
                  ) : stage === "winning" ? (
                    <Zap size={24} />
                  ) : (
                    <Wifi size={24} />
                  )}
                  {stageConfig[stage].title}
                </motion.div>
              </motion.div>

              {/* Main Grid */}
              <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0">
                {/* Main Slip - 7 cols */}
                <div className="lg:col-span-7 flex flex-col">
                  <FloatingCard delay={0.1} intensity={0.5} className="flex-1">
                    <motion.div
                      className={`h-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border overflow-hidden ${
                        stage === "celebration"
                          ? "border-yellow-500/50 shadow-xl shadow-yellow-500/20"
                          : "border-white/10"
                      }`}
                      animate={
                        stage === "celebration" ? { scale: [1, 1.02, 1] } : {}
                      }
                      transition={{
                        duration: 0.5,
                        repeat: stage === "celebration" ? 2 : 0,
                      }}
                    >
                      {/* Post Header */}
                      <div className="p-4 border-b border-white/10 bg-white/5">
                        <div className="flex items-center gap-3">
                          <motion.div
                            className="w-12 h-12 bg-gradient-to-br from-primary to-green-400 rounded-full flex items-center justify-center text-white font-bold text-lg"
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            {userInitial}
                          </motion.div>
                          <div className="flex-1">
                            <div className="font-semibold text-white">
                              @{userName}
                            </div>
                            <div className="text-xs text-gray-400 flex items-center gap-1">
                              <motion.span
                                animate={{ opacity: [1, 0.5, 1] }}
                                transition={{ duration: 1, repeat: Infinity }}
                                className="w-2 h-2 bg-red-500 rounded-full"
                              />
                              Live now
                            </div>
                          </div>
                          {/* Streak Badge instead of sportsbook */}
                          <div className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-full text-sm font-medium flex items-center gap-1">
                            <Flame size={14} />
                            {streak} Pick Streak
                          </div>
                        </div>
                      </div>

                      {/* Picks Content */}
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 bg-primary/20 text-primary rounded-lg text-sm font-medium">
                              3-Leg Picks
                            </span>
                            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full font-medium">
                              Rep: {mockPicksData.user.reputation}
                            </span>
                          </div>
                          {/* Community confidence instead of potential payout */}
                          <div className="text-right">
                            <div className="text-xs text-gray-500">
                              Community Confidence
                            </div>
                            <div className="text-xl font-bold text-primary">
                              87%
                            </div>
                          </div>
                        </div>

                        {/* Picks */}
                        <div className="space-y-2">
                          {picks.map((pick, index) => (
                            <motion.div
                              key={index}
                              initial={{ x: -20, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: 0.2 + index * 0.1 }}
                              className={`p-3 rounded-xl border transition-all ${
                                pick.status === "won"
                                  ? "bg-green-500/20 border-green-500/50"
                                  : "bg-white/5 border-white/10"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium text-white text-sm">
                                    {pick.team} vs {pick.opponent}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {pick.pick}
                                  </div>
                                </div>
                                <AnimatePresence mode="wait">
                                  {pick.status === "won" ? (
                                    <motion.div
                                      initial={{ scale: 0, rotate: -180 }}
                                      animate={{ scale: 1, rotate: 0 }}
                                      className="flex items-center gap-1 text-green-400 font-bold text-sm"
                                    >
                                      <CheckCircle size={18} />
                                      HIT
                                    </motion.div>
                                  ) : (
                                    <motion.div
                                      animate={{ opacity: [0.5, 1, 0.5] }}
                                      transition={{
                                        duration: 1.5,
                                        repeat: Infinity,
                                      }}
                                      className="flex items-center gap-1 text-yellow-500 text-xs"
                                    >
                                      <Clock size={14} />
                                      Live
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </motion.div>
                          ))}
                        </div>

                        {/* Celebration Message - Shows streak instead of money won */}
                        {stage === "celebration" && (
                          <motion.div
                            initial={{ scale: 0, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            className="mt-4 p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl text-center"
                          >
                            <div className="flex items-center justify-center gap-2 text-yellow-400 font-bold text-lg">
                              <Flame size={24} />
                              ALL PICKS HIT!
                              <Flame size={24} />
                            </div>
                            <div className="text-orange-300 text-sm mt-1">
                              Streak extended to {streak}! 🔥
                            </div>
                          </motion.div>
                        )}
                      </div>

                      {/* Engagement Bar */}
                      <div className="px-4 py-3 bg-white/5 border-t border-white/10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-5">
                            <motion.div
                              className="flex items-center gap-1.5 text-red-400"
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 0.2 }}
                              key={likes}
                            >
                              <Heart size={18} fill="currentColor" />
                              <span className="font-medium">{likes}</span>
                            </motion.div>
                            <motion.div
                              className="flex items-center gap-1.5 text-blue-400"
                              key={comments}
                            >
                              <MessageCircle size={18} />
                              <span className="font-medium">{comments}</span>
                            </motion.div>
                            <Share2 size={18} className="text-gray-500" />
                          </div>
                          <motion.div
                            className="flex items-center gap-1.5 text-primary font-medium"
                            key={tails}
                          >
                            <Users size={18} />
                            <span>{tails} tailing</span>
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  </FloatingCard>
                </div>

                {/* Side Panels - 5 cols */}
                <div className="lg:col-span-5 flex flex-col gap-4 min-h-0">
                  {/* Live Chat */}
                  <FloatingCard
                    delay={0.2}
                    intensity={0.3}
                    className="flex-1 min-h-0"
                  >
                    <div className="h-full bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-2xl border border-blue-500/20 overflow-hidden flex flex-col">
                      <div className="p-3 border-b border-white/10 bg-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MessageCircle
                              size={16}
                              className="text-blue-400"
                            />
                            <span className="font-semibold text-white text-sm">
                              Live Chat
                            </span>
                          </div>
                          <motion.span
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="flex items-center gap-1 text-green-400 text-xs"
                          >
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                            Live
                          </motion.span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
                        <AnimatePresence>
                          {chatMessages.map((msg, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -20, scale: 0.9 }}
                              animate={{ opacity: 1, x: 0, scale: 1 }}
                              className="bg-white/5 rounded-lg p-2"
                            >
                              <span className="text-primary font-medium text-xs">
                                {msg.user}
                              </span>
                              <p className="text-gray-300 text-sm">{msg.msg}</p>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                        {chatMessages.length === 0 && (
                          <div className="flex items-center justify-center h-full">
                            <motion.div
                              animate={{ opacity: [0.3, 0.6, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              className="text-gray-500 text-sm"
                            >
                              Waiting for messages...
                            </motion.div>
                          </div>
                        )}
                      </div>
                    </div>
                  </FloatingCard>

                  {/* Live News */}
                  <FloatingCard
                    delay={0.3}
                    intensity={0.3}
                    className="flex-1 min-h-0"
                  >
                    <div className="h-full bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-2xl border border-orange-500/20 overflow-hidden flex flex-col">
                      <div className="p-3 border-b border-white/10 bg-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Newspaper size={16} className="text-orange-400" />
                            <span className="font-semibold text-white text-sm">
                              Live News
                            </span>
                          </div>
                          <motion.span
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.5, repeat: Infinity }}
                            className="text-orange-400"
                          >
                            <Zap size={14} />
                          </motion.span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
                        <AnimatePresence>
                          {newsHeadlines.map((news, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, y: 20, scale: 0.9 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              className="bg-white/5 rounded-lg p-2"
                            >
                              <div className="flex items-start gap-2">
                                <TrendingUp
                                  size={14}
                                  className="text-orange-400 mt-0.5 flex-shrink-0"
                                />
                                <span className="text-gray-300 text-sm">
                                  {news.headline}
                                </span>
                              </div>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                        {newsHeadlines.length === 0 && (
                          <div className="flex items-center justify-center h-full">
                            <motion.div
                              animate={{ opacity: [0.3, 0.6, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              className="text-gray-500 text-sm"
                            >
                              Loading updates...
                            </motion.div>
                          </div>
                        )}
                      </div>
                    </div>
                  </FloatingCard>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Welcome Screen */}
        {stage === "welcome" && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="text-center max-w-2xl px-6">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 150,
                  damping: 15,
                  delay: 0.2,
                }}
                className="mb-8"
              >
                <div className="w-36 h-36 mx-auto bg-gradient-to-br from-primary via-green-400 to-emerald-500 rounded-[2rem] flex items-center justify-center shadow-2xl shadow-primary/50">
                  <span className="text-6xl font-black text-white">PG</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-5xl md:text-7xl font-black text-white mb-3"
              >
                Welcome to
              </motion.h1>

              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="text-5xl md:text-7xl font-black bg-gradient-to-r from-primary via-green-400 to-emerald-400 bg-clip-text text-transparent mb-6"
              >
                ParlayGround
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="text-xl text-gray-400 mb-8"
              >
                The Digital Home for Sports Fans
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1 }}
                className="mb-10"
              >
                <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl">
                  <Crown className="text-yellow-500" size={28} />
                  <span className="text-yellow-400 font-bold text-xl">
                    You're an OG Member
                  </span>
                  <Crown className="text-yellow-500" size={28} />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="flex justify-center gap-8 mb-10 flex-wrap"
              >
                {[
                  { icon: <Flame size={20} />, label: "SweatRooms" },
                  { icon: <Gift size={20} />, label: "GroundTokens" },
                  { icon: <Users size={20} />, label: "Community" },
                  { icon: <Trophy size={20} />, label: "Leaderboards" },
                ].map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3 + i * 0.1 }}
                    className="flex items-center gap-2 text-gray-400"
                  >
                    <span className="text-primary">{item.icon}</span>
                    <span>{item.label}</span>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
              >
                <Button
                  variant="primary"
                  size="lg"
                  icon={<ArrowRight size={24} />}
                  onClick={handleComplete}
                  className="px-12 py-5 text-xl font-bold shadow-2xl shadow-primary/40 hover:shadow-primary/60 transition-all hover:scale-105"
                >
                  Enter the OG Hub
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================
// OG HUB CHAT COMPONENT WITH TANSTACK QUERY
// ============================================

interface OGChatProps {
  className?: string;
}

// Hook to subscribe to Firebase realtime data with TanStack Query
const useFirebaseChat = (dbPath: string) => {
  const queryClient = useQueryClient();

  // Set up realtime listener
  useEffect(() => {
    const messagesRef = ref(database, dbPath);

    const handleData = (snapshot: any) => {
      const data = snapshot.val();
      if (data) {
        const messageList = Object.entries(data).map(
          ([key, value]: [string, any]) => ({
            messageId: key,
            ...value,
          })
        );
        messageList.sort((a: any, b: any) => a.createdAt - b.createdAt);
        // Update the query cache with the latest data
        queryClient.setQueryData(["ogHubChat", dbPath], messageList.slice(-50));
      } else {
        queryClient.setQueryData(["ogHubChat", dbPath], []);
      }
    };

    const handleError = (error: any) => {
      console.error("Firebase connection error:", error);
      queryClient.setQueryData(["ogHubChat", dbPath], undefined);
    };

    onValue(messagesRef, handleData, handleError);

    return () => off(messagesRef);
  }, [dbPath, queryClient]);

  // Use query to get the cached data and track connection state
  return useQuery({
    queryKey: ["ogHubChat", dbPath],
    queryFn: () => {
      // This won't actually be called since we're using setQueryData
      // But we need it for the query to be valid
      return new Promise<OGHubMessage[]>((resolve) => {
        const messagesRef = ref(database, dbPath);
        onValue(
          messagesRef,
          (snapshot) => {
            const data = snapshot.val();
            if (data) {
              const messageList = Object.entries(data).map(
                ([key, value]: [string, any]) => ({
                  messageId: key,
                  ...value,
                })
              );
              messageList.sort((a: any, b: any) => a.createdAt - b.createdAt);
              resolve(messageList.slice(-50));
            } else {
              resolve([]);
            }
          },
          { onlyOnce: true }
        );
      });
    },
    staleTime: Infinity, // Data is always fresh (realtime updates)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

const OGChat: React.FC<OGChatProps> = ({ className = "" }) => {
  const { user } = useAuth();
  const { isTestMode } = useTestMode();
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const dbPath = isTestMode ? "test-mode/v1/config/ogHubChat" : "v1/config/ogHubChat";

  // Use TanStack Query for Firebase data
  const {
    data: messages = [],
    isLoading,
    isError,
    isFetched,
  } = useFirebaseChat(dbPath);

  // Connection status based on query state
  const isConnected = isFetched && !isError;
  const isConnecting = isLoading && !isFetched;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user || sending) return;
    setSending(true);
    try {
      const messagesRef = ref(database, dbPath);
      await push(messagesRef, {
        userId: user.uid,
        username: user.username,
        userPhotoURL: user.photoURL || "",
        content: newMessage.trim(),
        type: "chat",
        createdAt: Date.now(),
      });
      setNewMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`flex flex-col h-full bg-white dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="p-4 border-b border-border-light dark:border-border-dark bg-gradient-to-r from-primary/10 to-green-500/10 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle size={20} className="text-primary" />
            <h3 className="font-bold">OG Chat</h3>
          </div>
          <div
            className={`flex items-center gap-1 text-xs ${
              isConnected
                ? "text-green-500"
                : isError
                ? "text-red-500"
                : "text-yellow-500"
            }`}
          >
            {isConnected ? (
              <>
                <motion.span
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-1.5 h-1.5 bg-green-500 rounded-full"
                />
                <Wifi size={12} />
                <span>Live</span>
              </>
            ) : isError ? (
              <>
                <WifiOff size={12} />
                <span>Offline</span>
              </>
            ) : (
              <>
                <Loader2 size={12} className="animate-spin" />
                <span>Connecting...</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
        {isConnecting ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <Loader2 size={32} className="text-primary mb-2 animate-spin" />
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Connecting to chat...
            </p>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <WifiOff size={32} className="text-red-500 mb-2" />
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Failed to connect. Check your connection.
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <MessageSquare
              size={32}
              className="text-text-secondary-light dark:text-text-secondary-dark mb-2 opacity-50"
            />
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Be the first to say something!
            </p>
          </div>
        ) : (
          messages.map((msg) => (
            <motion.div
              key={msg.messageId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-2 ${
                msg.userId === user?.uid ? "flex-row-reverse" : ""
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-green-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {msg.username?.charAt(0).toUpperCase() || "?"}
              </div>
              <div
                className={`max-w-[80%] ${
                  msg.userId === user?.uid ? "text-right" : ""
                }`}
              >
                {msg.userId !== user?.uid && (
                  <span className="text-xs font-medium mb-0.5 block">
                    @{msg.username}
                  </span>
                )}
                <div
                  className={`inline-block px-3 py-2 rounded-2xl text-sm ${
                    msg.userId === user?.uid
                      ? "bg-primary text-white rounded-br-md"
                      : "bg-surface-light dark:bg-gray-800 rounded-bl-md"
                  }`}
                >
                  {msg.content}
                </div>
                <p className="text-[10px] text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                  {formatDistanceToNow(msg.createdAt, { addSuffix: true })}
                </p>
              </div>
            </motion.div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-border-light dark:border-border-dark flex-shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
            placeholder="Type a message..."
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-gray-800 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
          <Button
            variant="primary"
            size="sm"
            icon={
              sending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )
            }
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending || !isConnected}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================
// FEEDBACK FORM COMPONENT
// ============================================

interface FeedbackFormProps {
  className?: string;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ className = "" }) => {
  const { user } = useAuth();
  const { isTestMode } = useTestMode();
  const [feedbackType, setFeedbackType] = useState<
    "idea" | "question" | "feedback"
  >("idea");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  const dbPath = isTestMode ? "test-mode/v1/config/ogFeedback" : "v1/config/ogFeedback";

  const handleSubmit = async () => {
    if (!content.trim() || !user || sending) return;
    setSending(true);
    try {
      // Save to Firebase
      const feedbackRef = ref(database, dbPath);
      await push(feedbackRef, {
        userId: user.uid,
        username: user.username,
        userEmail: user.email || "",
        type: feedbackType,
        content: content.trim(),
        createdAt: Date.now(),
        status: "pending",
      });

      // Send via EmailJS
      await emailjs.send(
        "service_mberqr3",
        "template_pwb8oxb",
        {
          from_name: user.username || user.email?.split("@")[0] || "OG Member",
          from_email: user.email || "no-email@parlayground.com",
          message: `TYPE: ${feedbackType.toUpperCase()}\n\n${content.trim()}`,
        },
        { publicKey: "FqhZGqNfL3s-P9D-7" }
      );

      setContent("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setSending(false);
    }
  };

  const typeOptions = [
    {
      type: "idea",
      label: "Idea",
      icon: <Lightbulb size={16} />,
      color: "text-yellow-500",
      bg: "bg-yellow-500/10",
      activeBg: "bg-yellow-500",
    },
    {
      type: "question",
      label: "Question",
      icon: <HelpCircle size={16} />,
      color: "text-blue-500",
      bg: "bg-blue-500/10",
      activeBg: "bg-blue-500",
    },
    {
      type: "feedback",
      label: "Feedback",
      icon: <MessageSquare size={16} />,
      color: "text-purple-500",
      bg: "bg-purple-500/10",
      activeBg: "bg-purple-500",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className={`card p-6 ${className}`}
    >
      <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
        <Lightbulb size={20} className="text-yellow-500" />
        Share Your Thoughts
      </h3>
      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
        Help us build the best sports community together
      </p>

      {success ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-green-500"
        >
          <CheckCircle size={24} />
          <div>
            <p className="font-medium">Thanks for your input!</p>
            <p className="text-sm opacity-80">
              We'll review it and get back to you.
            </p>
          </div>
        </motion.div>
      ) : (
        <>
          {/* Type Selector */}
          <div className="flex gap-2 mb-4">
            {typeOptions.map((option) => (
              <button
                key={option.type}
                onClick={() => setFeedbackType(option.type as any)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  feedbackType === option.type
                    ? `${option.activeBg} text-white`
                    : `${option.bg} ${option.color} hover:opacity-80`
                }`}
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>

          {/* Content Input */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={
              feedbackType === "idea"
                ? "Describe your idea for ParlayGround..."
                : feedbackType === "question"
                ? "What would you like to know?"
                : "Share your feedback or suggestions..."
            }
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-border-light dark:border-border-dark bg-surface-light dark:bg-gray-800 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none mb-4"
          />

          {/* Submit */}
          <Button
            variant="primary"
            size="md"
            icon={
              sending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )
            }
            onClick={handleSubmit}
            disabled={!content.trim() || sending}
            className="w-full"
          >
            {sending ? "Sending..." : "Submit"}
          </Button>
        </>
      )}
    </motion.div>
  );
};

// ============================================
// MAIN OG HUB PAGE
// ============================================

export const OGHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isTestMode } = useTestMode();
  const [showSimulation, setShowSimulation] = useState(false);
  const [hasCompletedSimulation, setHasCompletedSimulation] = useState<boolean | null>(null);
  const [isLoadingSimulationStatus, setIsLoadingSimulationStatus] = useState(true);

  const userName = user?.username || "you";
  const userInitial = userName.charAt(0).toUpperCase();

  // Fetch simulation completion status from Firebase
  useEffect(() => {
    const fetchSimulationStatus = async () => {
      if (!user?.uid) {
        setIsLoadingSimulationStatus(false);
        return;
      }

      try {
        const basePath = isTestMode ? "test-mode/v1" : "v1";
        const path = `${basePath}/users/profiles/${user.uid}/hasSeenOGHubSimulation`;
        const snapshot = await dbGet(path);
        const hasCompleted = snapshot.exists() && snapshot.val() === true;
        setHasCompletedSimulation(hasCompleted);
      } catch (error) {
        console.error("Failed to fetch simulation status:", error);
        setHasCompletedSimulation(false);
      } finally {
        setIsLoadingSimulationStatus(false);
      }
    };

    fetchSimulationStatus();
  }, [user?.uid, isTestMode]);

  // Show simulation on first visit (after loading completes)
  useEffect(() => {
    if (!isLoadingSimulationStatus && hasCompletedSimulation === false) {
      setShowSimulation(true);
    }
  }, [isLoadingSimulationStatus, hasCompletedSimulation]);

  const handleSimulationComplete = async () => {
    if (user?.uid) {
      try {
        const basePath = isTestMode ? "test-mode/v1" : "v1";
        const path = `${basePath}/users/profiles/${user.uid}`;
        await dbUpdate(path, { hasSeenOGHubSimulation: true });
      } catch (error) {
        console.error("Failed to save simulation status:", error);
      }
    }
    setHasCompletedSimulation(true);
    setShowSimulation(false);
  };

  return (
    <>
      {/* Simulation Overlay */}
      <AnimatePresence>
        {showSimulation && (
          <SimulationOverlay
            isOpen={showSimulation}
            onClose={() => setShowSimulation(false)}
            onComplete={handleSimulationComplete}
            userName={userName}
            userInitial={userInitial}
          />
        )}
      </AnimatePresence>

      {/* Main OG Hub */}
      <div className="min-h-screen bg-gradient-to-b from-surface-light to-white dark:from-surface-dark dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div
                  className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <Crown size={24} className="text-white" />
                </motion.div>
                <div>
                  <h1 className="text-2xl font-bold">OG Hub</h1>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Welcome back, {userName}!
                  </p>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                icon={<Play size={16} />}
                onClick={() => setShowSimulation(true)}
                className="hover:bg-primary hover:text-white transition-all"
              >
                Watch Demo
              </Button>
            </div>
          </motion.div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content - 3/4 */}
            <div className="lg:col-span-3 space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  {
                    label: "OG Status",
                    value: "Active",
                    icon: <Crown size={20} />,
                    color: "text-yellow-500",
                    bg: "bg-yellow-500/10",
                  },
                  {
                    label: "GT Balance",
                    value: "View",
                    icon: <Gift size={20} />,
                    color: "text-primary",
                    bg: "bg-primary/10",
                    link: "/wallet",
                  },
                  {
                    label: "Community",
                    value: "Growing",
                    icon: <Users size={20} />,
                    color: "text-blue-500",
                    bg: "bg-blue-500/10",
                  },
                  {
                    label: "Features",
                    value: "Soon™",
                    icon: <Rocket size={20} />,
                    color: "text-purple-500",
                    bg: "bg-purple-500/10",
                  },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    onClick={() => stat.link && navigate(stat.link)}
                    className={`card p-4 ${stat.link ? "cursor-pointer" : ""}`}
                  >
                    <div
                      className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center ${stat.color} mb-2`}
                    >
                      {stat.icon}
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      {stat.label}
                    </p>
                    <p className="font-bold">{stat.value}</p>
                  </motion.div>
                ))}
              </div>

              {/* CTA Cards */}
              <div className="grid md:grid-cols-2 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  className="card p-6 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-2 border-indigo-500/20 cursor-pointer"
                  onClick={() =>
                    window.open(
                      "https://discord.com/invite/kayaMSnjR",
                      "_blank"
                    )
                  }
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg
                        viewBox="0 0 24 24"
                        className="w-8 h-8 text-white"
                        fill="currentColor"
                      >
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Join Discord</h3>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                        Connect with OGs, get updates, help shape features
                      </p>
                      <span className="inline-flex items-center gap-1 text-indigo-500 font-medium text-sm">
                        Join Now <ChevronRight size={16} />
                      </span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="card p-6 bg-gradient-to-br from-green-500/10 to-primary/10 border-2 border-primary/20 cursor-pointer"
                  onClick={() => navigate("/wallet")}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                      <Gift size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">GroundTokens</h3>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                        Check balance, view transactions, get more GT
                      </p>
                      <span className="inline-flex items-center gap-1 text-primary font-medium text-sm">
                        Go to Wallet <ChevronRight size={16} />
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Coming Soon */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card p-6"
              >
                <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
                  <Rocket size={20} className="text-primary" />
                  Coming Soon for OGs
                </h3>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
                  The all in one system is on the way...
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      icon: <Flame size={20} />,
                      title: "SweatRooms",
                      desc: "Live watch parties",
                      color: "text-orange-500",
                      bg: "bg-orange-500/10",
                    },
                    {
                      icon: <TrendingUp size={20} />,
                      title: "Leaderboards",
                      desc: "Compete with others",
                      color: "text-blue-500",
                      bg: "bg-blue-500/10",
                    },
                    {
                      icon: <Gamepad2 size={20} />,
                      title: "Daily Trivia",
                      desc: "Win GT daily",
                      color: "text-purple-500",
                      bg: "bg-purple-500/10",
                    },
                    {
                      icon: <Store size={20} />,
                      title: "Token Store",
                      desc: "Spend your GT",
                      color: "text-pink-500",
                      bg: "bg-pink-500/10",
                    },
                    {
                      icon: <Shield size={20} />,
                      title: "Verified Slips",
                      desc: "Share your bets",
                      color: "text-green-500",
                      bg: "bg-green-500/10",
                    },
                    {
                      icon: <Users size={20} />,
                      title: "Tail System",
                      desc: "Follow the best",
                      color: "text-cyan-500",
                      bg: "bg-cyan-500/10",
                    },
                  ].map((feature) => (
                    <motion.div
                      key={feature.title}
                      className="flex items-center gap-3 p-3 rounded-xl bg-surface-light dark:bg-gray-800/50"
                      whileHover={{ x: 5 }}
                    >
                      <div
                        className={`w-10 h-10 ${feature.bg} rounded-lg flex items-center justify-center ${feature.color}`}
                      >
                        {feature.icon}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{feature.title}</p>
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          {feature.desc}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Feedback Form */}
              <FeedbackForm />

              {/* About */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card p-6"
              >
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Star size={20} className="text-primary" />
                  What is ParlayGround?
                </h3>
                <p className="text-text-secondary-light dark:text-text-secondary-dark leading-relaxed">
                  <strong className="text-text-primary-light dark:text-text-primary-dark">
                    ParlayGround is the digital home for sports fans.
                  </strong>{" "}
                  Share betting slips, watch games in SweatRooms, earn
                  GroundTokens, and build your sports identity. As an OG, you're
                  shaping the future of sports community.
                </p>
              </motion.div>
            </div>

            {/* Chat Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="sticky top-24 h-[calc(100vh-8rem)]"
              >
                <OGChat className="h-full" />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OGHubPage;
